import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import { Package, Save, Trash2 } from "lucide-react";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  Page,
  SegmentedControl,
  Select,
  Table,
  type Column,
} from "@marekl/adminer/components";
import { useSetTopbarTitle } from "@marekl/adminer/shell";
import { ListCard } from "../../components/ListCard";
import styles from "./ProductsPage.module.css";
import { useConfirm, useDebouncedSearchFilter, useOptionalFilters } from "@marekl/adminer/hooks";
import { apiErrorToFormErrors, rootErrorMessage } from "@marekl/adminer/forms";
import { notifySuccess } from "@marekl/adminer/api";
import { useCurrentCompany } from "../../lib/company";
import { useDeleteProduct, useProduct, useProductList, useSaveProduct, type Product, type ProductInput } from "../../lib/queries/products";
import { usePagedList } from "../../lib/usePagedList";
import { DecimalInput } from "../../components/DecimalInput";
import { currencySign, formatMoney } from "../../lib/money";
import { previewTotals } from "../../lib/queries/documents";

const UNITS = ["ks", "hod", "den", "m²", "km"];
const RATES = ["0", "12", "21"];

/** The price with VAT of one unit, from the server (one money implementation). */
function useUnitPriceWithVat(companyId: string, value: number | null, vatRate: number, inclVat: boolean) {
  const [total, setTotal] = useState<number>();
  useEffect(() => {
    if (value === null) {
      setTotal(undefined);
      return;
    }
    let live = true;
    const t = window.setTimeout(() => {
      previewTotals(companyId, [{ name: "-", quantity: 1000, unit_price: value, vat_rate: vatRate, incl_vat: inclVat }])
        .then((r) => live && setTotal(r.total_incl_vat))
        .catch(() => live && setTotal(undefined));
    }, 300);
    return () => {
      live = false;
      window.clearTimeout(t);
    };
  }, [companyId, value, vatRate, inclVat]);
  return total;
}

interface ProductForm {
  name: string;
  unit: string;
  value: number | null;
  currency: "CZK" | "EUR";
  vat_rate: string;
  incl_vat: boolean;
}

function ProductForm({ product }: { product?: Product }) {
  const { id, company, can } = useCurrentCompany();
  const navigate = useNavigate();
  const save = useSaveProduct(id);
  const del = useDeleteProduct(id);
  const confirm = useConfirm();
  const payer = company.billing_details.vat_payer;
  const { register, control, handleSubmit, setError, formState } = useForm<ProductForm>({
    mode: "onTouched",
    defaultValues: product
      ? { name: product.name, unit: product.unit, value: product.price.value, currency: product.price.currency, vat_rate: String(product.price.vat_rate), incl_vat: product.price.incl_vat }
      : { name: "", unit: "ks", value: null, currency: "CZK", vat_rate: String(payer ? company.default_vat_rate : 0), incl_vat: false },
  });
  const [currency, unit, value, vatRate, inclVat] = useWatch({ control, name: ["currency", "unit", "value", "vat_rate", "incl_vat"] });
  const withVat = useUnitPriceWithVat(id, value, payer ? Number(vatRate) : 0, inclVat);
  const [otherCurrency, setOtherCurrency] = useState(currency !== "CZK");

  async function onSubmit(v: ProductForm) {
    const input: ProductInput = {
      name: v.name.trim(),
      unit: v.unit.trim(),
      price: { value: v.value ?? 0, currency: v.currency, vat_rate: payer ? Number(v.vat_rate) : 0, incl_vat: v.incl_vat },
    };
    try {
      await save.mutateAsync({ id: product?.id, input });
      notifySuccess("Produkt byl uložen.");
      navigate(`/c/${id}/products`);
    } catch (err) {
      apiErrorToFormErrors<ProductForm>(err, setError, {
        fallbackMessage: "Uložení se nepovedlo.",
        fieldMap: (f) => (f.startsWith("price.") ? (f === "price.value" ? "value" : (f.slice(6) as keyof ProductForm)) : (f as keyof ProductForm)),
      });
    }
  }

  const editor = can("editor");
  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)}>
      <Card>
        <Card.Header title={product ? "Úprava produktu" : "Nový produkt"} subtitle={product?.name} />
        <Card.Content>
          <fieldset className={styles.fields} disabled={!editor}>
            {rootErrorMessage(formState.errors) && <Alert variant="err">{rootErrorMessage(formState.errors)}</Alert>}
            <Input label="Název" required className={styles.name} {...register("name", { required: "Povinné pole" })} error={formState.errors.name?.message} />
            <Controller
              name="unit"
              control={control}
              render={({ field }) => (
                <div className={styles.unit}>
                  <SegmentedControl
                    label="Jednotka"
                    options={UNITS.map((u) => ({ value: u, label: u }))}
                    value={UNITS.includes(field.value) ? field.value : ""}
                    onChange={field.onChange}
                    wrap
                  />
                  <Input aria-label="Jiná jednotka" placeholder="jiná…" className={styles.unitOther} value={UNITS.includes(field.value) ? "" : field.value} onChange={(e) => field.onChange(e.target.value)} onBlur={field.onBlur} />
                </div>
              )}
            />
            <div className={styles.price}>
              <Controller
                name="value"
                control={control}
                rules={{ validate: (v) => v !== null || "Povinné pole" }}
                render={({ field, fieldState }) => (
                  <DecimalInput label="Cena za jednotku" required scale={2} suffix={currencySign(currency)} allowNegative value={field.value} onChange={field.onChange} onBlur={field.onBlur} error={fieldState.error?.message} className={styles.priceInput} />
                )}
              />
              {payer && (
                <Controller
                  name="incl_vat"
                  control={control}
                  render={({ field }) => (
                    <SegmentedControl
                      aria-label="Cena je"
                      options={[
                        { value: "excl", label: "bez DPH" },
                        { value: "incl", label: "s DPH" },
                      ]}
                      value={field.value ? "incl" : "excl"}
                      onChange={(v) => field.onChange(v === "incl")}
                    />
                  )}
                />
              )}
            </div>
            {payer && (
              <Controller
                name="vat_rate"
                control={control}
                render={({ field }) => (
                  <SegmentedControl
                    label="Sazba DPH"
                    options={Array.from(new Set([...RATES, field.value])).map((r) => ({ value: r, label: `${r} %` }))}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            )}
            {payer && withVat !== undefined && (
              <div className={styles.withVat}>
                <span>Cena s DPH za 1 {unit || "jednotku"}</span>
                <b>{formatMoney(withVat, currency)}</b>
              </div>
            )}
            {otherCurrency ? (
              <Select label="Měna" className={styles.currency} {...register("currency")}>
                <option value="CZK">CZK</option>
                <option value="EUR">EUR</option>
              </Select>
            ) : (
              <div>
                <Button type="button" size="sm" weight="ghost" onClick={() => setOtherCurrency(true)}>
                  cena v jiné měně
                </Button>
              </div>
            )}
          </fieldset>
        </Card.Content>
        {editor && (
          <Card.Footer>
            <div className={styles.footer}>
              {product && (
                <Button
                  type="button"
                  variant="danger"
                  weight="ghost"
                  startIcon={<Trash2 size={16} aria-hidden="true" />}
                  onClick={async () => {
                    if (await confirm({ title: "Smazat produkt", description: product.name, confirmLabel: "Smazat", variant: "danger" })) {
                      await del.mutateAsync(product.id);
                      navigate(`/c/${id}/products`);
                    }
                  }}
                >
                  smazat produkt
                </Button>
              )}
              <span className={styles.spacer} />
              <Button type="button" variant="primary" weight="outline" onClick={() => navigate(`/c/${id}/products`)}>
                zrušit
              </Button>
              <Button type="submit" variant="primary" startIcon={<Save size={16} aria-hidden="true" />} loading={save.isPending}>
                uložit produkt
              </Button>
            </div>
          </Card.Footer>
        )}
      </Card>
    </form>
  );
}

/** /c/:companyId/products/new and /c/:companyId/products/:productId */
export function ProductEditPage() {
  const { id } = useCurrentCompany();
  const { productId } = useParams();
  const product = useProduct(id, productId === "new" ? undefined : productId);
  useSetTopbarTitle("Produkty");
  return (
    <Page>
      <Page.Content>
        {productId !== "new" && product.isLoading ? (
          <LoadingState />
        ) : product.error ? (
          <ErrorState title="Produkt se nepodařilo načíst" error={product.error} onRetry={product.refetch} />
        ) : (
          <ProductForm key={product.data?.id ?? "new"} product={product.data} />
        )}
      </Page.Content>
    </Page>
  );
}

export function ProductsPage() {
  const { id, can, company } = useCurrentCompany();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") ?? "";
  useSetTopbarTitle("Produkty");
  const list = usePagedList(useProductList(id), { q: q || undefined }, {
    searchParams,
    setSearchParams,
    nounOf: ["produktu", "produktů"],
  });
  const { draft, setDraft } = useDebouncedSearchFilter({
    value: q,
    onCommit: (next) => list.filterReset((p) => (next ? p.set("q", next) : p.delete("q"))),
  });
  const payer = company.billing_details.vat_payer;

  const columns: Column<Product>[] = [
    { key: "name", header: "Název", primary: true, render: (p) => p.name, linkTo: (p) => `/c/${id}/products/${p.id}` },
    { key: "unit", header: "Jednotka", render: (p) => p.unit || "—" },
    { key: "price", header: "Jednotková cena", numeric: true, render: (p) => formatMoney(p.price.value, p.price.currency) },
    ...(payer
      ? [{ key: "vat", header: "DPH", render: (p: Product) => `${p.price.vat_rate} %${p.price.incl_vat ? " (vč. DPH)" : ""}` }]
      : []),
  ];

  const filters = useOptionalFilters({
    filters: [
      {
        key: "q",
        label: "Název",
        alwaysOn: true,
        wide: true,
        active: !!q,
        onClear: () => list.filterReset((p) => p.delete("q")),
        render: () => <Input label="Název" value={draft} onChange={(e) => setDraft(e.target.value)} />,
      },
    ],
  });

  return (
    <Page>
      <Page.Content>
        <ListCard
          title="Seznam produktů"
          onCreate={can("editor") ? () => navigate(`/c/${id}/products/new`) : undefined}
          createLabel="nový"
          filters={filters}
          pagination={list.pagination}
          summary={list.summary}
        >
          <Table<Product>
            cards
            columns={columns}
            rows={list.data?.items ?? []}
            rowKey={(p) => p.id}
            isLoading={list.isLoading}
            error={list.error}
            onRetry={list.refetch}
            empty={<EmptyState icon={Package} title={q ? "Nic nenalezeno" : "Zatím žádné produkty"} />}
          />
        </ListCard>
      </Page.Content>
    </Page>
  );
}
